import type { Ref } from '@vue/composition-api';
import { ref } from '@vue/composition-api';
import { useStorage } from '@vueuse/core';
import type { VerifyLinkResponse } from '@getaccept/lib-shared/src/universal-link/types/verify-link-response';
import type { IdentifyRecipientPayload } from '@getaccept/lib-shared/src/universal-link/types/identify-recipient-payload';
import { UniversalLinkHelper } from '@getaccept/lib-shared/src/universal-link/helpers/universal-link.helper';
import { UniversalLinkService } from '../../../api/universal-link/services/universal-link.service';

export interface UniversalLinkStorage {
  redirectURL: string;
}

export function useUniversalLink() {
  const loading: Ref<boolean> = ref(false);
  const failedToVerify: Ref<boolean> = ref(false);
  const failedRestricted: Ref<boolean> = ref(false);
  const verifyLinkResponse: Ref<VerifyLinkResponse | null> = ref(null);
  const universalLinkKey: string = UniversalLinkHelper.getUniversalLinkKey(window.location.href);
  const universalLinkStorage = useStorage<UniversalLinkStorage>(
    `universalLink:${universalLinkKey}`,
    {
      redirectURL: null,
    }
  );

  const verifyLink = async (universalId: string): Promise<void> => {
    loading.value = true;
    failedToVerify.value = false;
    try {
      verifyLinkResponse.value = await UniversalLinkService.verify(universalId);
      loading.value = false;
    } catch (e) {
      if (e.message === 'Document access denied, Restricted') {
        failedRestricted.value = true;
      }
      loading.value = false;
      failedToVerify.value = true;
      verifyLinkResponse.value = null;
    }
  };

  const loadingRedirectURL: Ref<boolean> = ref(false);
  const redirectURLError = ref(null);
  const getRedirectUrl = async (
    gdprConsent: boolean,
    documentId: string,
    recipient: IdentifyRecipientPayload
  ) => {
    loadingRedirectURL.value = true;
    redirectURLError.value = null;

    try {
      const url = await UniversalLinkService.identifyRecipient(gdprConsent, documentId, recipient);
      loadingRedirectURL.value = false;
      redirectURLError.value = null;
      window.location.assign(url);
      universalLinkStorage.value.redirectURL = url;
    } catch (error) {
      loadingRedirectURL.value = false;
      redirectURLError.value = error;
    }
  };
  return {
    verifyLink,
    loading,
    failedToVerify,
    verifyLinkResponse,
    getRedirectUrl,
    redirectURLError,
    loadingRedirectURL,
    failedRestricted,
    universalLinkStorage,
  };
}
